import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileText, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

function Home() {
    // Inputs
    const [resume, setResume] = useState(null);
    const [jdFile, setJdFile] = useState(null);
    const [jdText, setJdText] = useState("");
    const [activeTab, setActiveTab] = useState("file"); //  'file' or 'text'

    // Outputs
    const [matchScore, setMatchScore] = useState(null);
    const [question, setQuestion] = useState("");
    const [chatHistory, setChatHistory] = useState([]);

    // UI States
    const [loading, setLoading] = useState(false);
    const [chatLoading, setChatLoading] = useState(false);
    const [error, setError] = useState("");

    const handleUpload = async () => {
        if (!resume) {
            setError("Please upload a Resume PDF.");
            return;
        }
        if (activeTab === "file" && !jdFile) {
            setError("Please upload a Job Description PDF.");
            return;
        }
        if (activeTab === "text" && !jdText.trim()) {
            setError("Please paste the Job Description.");
            return;
        }

        setError("");
        setLoading(true);
        setMatchScore(null);

        const formData = new FormData();
        formData.append("resume", resume);

        if (activeTab === "file") {
            formData.append("jdFile", jdFile);
        } else {
            formData.append("jdText", jdText);
        }

        try {
            // 1. Upload & Analyze
            const res = await axios.post("https://resume-analyzer-api-rouge.vercel.app/upload", formData);
            setMatchScore(res.data.matchScore);
        } catch (err) {
            console.error(err);
            setError("Analysis failed. Check backend console.");
        }

        setLoading(false);
    };

    const askQuestion = async () => {
        if (!question.trim()) return;

        const currentQ = question;
        setQuestion("");
        setChatLoading(true);

        // Add user question to history immediately
        setChatHistory(prev => [...prev, { type: 'user', text: currentQ }]);

        try {
            const res = await axios.post("https://resume-analyzer-api-rouge.vercel.app/chat", { question: currentQ });
            setChatHistory(prev => [...prev, { type: 'bot', text: res.data.answer }]);
        } catch (err) {
            setChatHistory(prev => [...prev, { type: 'bot', text: "Sorry, I couldn't get an answer right now." }]);
        }

        setChatLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-gray-100 font-sans selection:bg-purple-500 selection:text-white">

            {/* Hero Section */}
            <div className="py-10 text-center">
                <h1 className="text-5xl font-extrabold tracking-tight mb-2 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 text-transparent bg-clip-text">
                    Resume AI Analyzer
                </h1>
                <p className="text-slate-400">Optimize your application with AI-powered insights</p>
            </div>

            <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-10 pb-20">

                {/* LEFT COLUMN: Upload Controls */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                >
                    <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl backdrop-blur-sm shadow-xl">

                        {/* 1. Resume Upload */}
                        <div className="mb-8">
                            <label className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider">
                                Candidate Resume (PDF)
                            </label>
                            <div className="relative group">
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={(e) => setResume(e.target.files[0])}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                />
                                <div className={`flex items-center justify-center p-6 border-2 border-dashed rounded-xl transition-all ${resume ? 'border-green-500 bg-green-500/10' : 'border-slate-700 hover:border-purple-500 hover:bg-slate-800'}`}>
                                    {resume ? (
                                        <span className="flex items-center text-green-400 font-medium"><CheckCircle className="w-5 h-5 mr-2" /> {resume.name}</span>
                                    ) : (
                                        <span className="flex items-center text-slate-400"><UploadCloud className="w-5 h-5 mr-2" /> Drop PDF or Click to Upload</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 2. JD Toggle (File vs Text) */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider">
                                Job Description
                            </label>

                            <div className="flex bg-slate-800 p-1 rounded-lg mb-4">
                                <button
                                    onClick={() => setActiveTab("file")}
                                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${activeTab === "file" ? "bg-slate-700 text-white shadow" : "text-slate-400 hover:text-white"}`}
                                >
                                    Upload File
                                </button>
                                <button
                                    onClick={() => setActiveTab("text")}
                                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${activeTab === "text" ? "bg-slate-700 text-white shadow" : "text-slate-400 hover:text-white"}`}
                                >
                                    Paste Text
                                </button>
                            </div>

                            {activeTab === "file" ? (
                                <div className="relative group">
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => setJdFile(e.target.files[0])}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    />
                                    <div className={`flex items-center justify-center p-6 border-2 border-dashed rounded-xl transition-all ${jdFile ? 'border-green-500 bg-green-500/10' : 'border-slate-700 hover:border-purple-500 hover:bg-slate-800'}`}>
                                        {jdFile ? (
                                            <span className="flex items-center text-green-400 font-medium"><CheckCircle className="w-5 h-5 mr-2" /> {jdFile.name}</span>
                                        ) : (
                                            <span className="flex items-center text-slate-400"><UploadCloud className="w-5 h-5 mr-2" /> Drop JD PDF here</span>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <textarea
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-slate-200 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
                                    rows="6"
                                    placeholder="Paste the job requirements here..."
                                    value={jdText}
                                    onChange={(e) => setJdText(e.target.value)}
                                />
                            )}
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center text-red-400 text-sm mb-4 bg-red-900/20 p-3 rounded-lg">
                                <AlertCircle className="w-4 h-4 mr-2" /> {error}
                            </div>
                        )}

                        {/* Action Button */}
                        <button
                            onClick={handleUpload}
                            disabled={loading}
                            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 transition-all disabled:opacity-50 flex justify-center items-center"
                        >
                            {loading ? <Loader2 className="animate-spin mr-2" /> : "Analyze Match"}
                        </button>

                    </div>
                </motion.div>

                {/* RIGHT COLUMN: Results */}
                <div className="space-y-6">
                    <AnimatePresence>
                        {matchScore !== null && (
                            <>
                                {/* Score Card */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl backdrop-blur-sm flex items-center justify-between shadow-xl"
                                >
                                    <div>
                                        <h3 className="text-slate-400 font-medium mb-1">Match Score</h3>
                                        <div className={`text-5xl font-bold ${matchScore > 70 ? 'text-green-400' : matchScore > 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                                            {matchScore}%
                                        </div>
                                    </div>
                                    <div className="h-20 w-20 rounded-full border-4 border-purple-500/30 flex items-center justify-center">
                                        <FileText className="w-8 h-8 text-purple-400" />
                                    </div>
                                </motion.div>

                                {/* Chat Interface */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-slate-900/50 border border-slate-800 rounded-3xl backdrop-blur-sm shadow-xl overflow-hidden flex flex-col h-[500px]"
                                >
                                    <div className="p-4 border-b border-slate-800 bg-slate-800/30">
                                        <h3 className="font-semibold text-slate-200 flex items-center">
                                            <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                                            AI Interviewer
                                        </h3>
                                    </div>

                                    {/* Chat History */}
                                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                        <div className="flex justify-start">
                                            <div className="bg-slate-800 text-slate-200 p-3 rounded-2xl rounded-tl-none max-w-[80%] text-sm">
                                                Hello! I've analyzed the resume. What would you like to know?
                                            </div>
                                        </div>

                                        {chatHistory.map((msg, i) => (
                                            <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`p-3 rounded-2xl text-sm max-w-[80%] ${msg.type === 'user' ? 'bg-purple-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 rounded-tl-none'}`}>
                                                    {msg.text}
                                                </div>
                                            </div>
                                        ))}

                                        {chatLoading && (
                                            <div className="flex justify-start">
                                                <div className="bg-slate-800 p-3 rounded-2xl rounded-tl-none">
                                                    <div className="flex space-x-1">
                                                        <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce"></div>
                                                        <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce delay-75"></div>
                                                        <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce delay-150"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Input Area */}
                                    <div className="p-4 bg-slate-900 border-t border-slate-800 flex gap-2">
                                        <input
                                            type="text"
                                            className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                                            placeholder="Ask about skills, experience..."
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && askQuestion()}
                                        />
                                        <button
                                            onClick={askQuestion}
                                            disabled={chatLoading}
                                            className="bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-xl transition-colors disabled:opacity-50"
                                        >
                                            <Send className="w-5 h-5" />
                                        </button>
                                    </div>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

export default Home;
